import React, { useState, useEffect } from 'react';
import { startRegistration, startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Fingerprint, Loader2, AlertCircle, CheckCircle, Trash2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

console.log("Backend URL is:", process.env.REACT_APP_BACKEND_URL);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Check if WebAuthn is supported
export const useWebAuthnSupport = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isPlatformAvailable, setIsPlatformAvailable] = useState(false);

  useEffect(() => {
    const checkSupport = async () => {
      if (!browserSupportsWebAuthn()) {
        setIsSupported(false);
        return;
      }
      
      setIsSupported(true);
      
      try {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setIsPlatformAvailable(available);
      } catch (e) {
        setIsPlatformAvailable(false);
      }
    };
    
    checkSupport();
  }, []);

  return { isSupported, isPlatformAvailable };
};

// WebAuthn Login Button Component
export const WebAuthnLoginButton = ({ email, onSuccess, onError, disabled }) => {
  const [loading, setLoading] = useState(false);
  const { isSupported, isPlatformAvailable } = useWebAuthnSupport();

  const handleWebAuthnLogin = async () => {
    if (!email) {
      toast.error('Please enter your email first');
      return;
    }

    setLoading(true);
    try {
      const optionsRes = await fetch(`${API_URL}/api/auth/webauthn/login/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!optionsRes.ok) {
        const error = await optionsRes.json();
        throw new Error(error.detail || 'Failed to get authentication options');
      }

      const { options } = await optionsRes.json();
      const assertion = await startAuthentication(options);

      const verifyRes = await fetch(`${API_URL}/api/auth/webauthn/login/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, assertion }),
      });

      if (!verifyRes.ok) {
        const error = await verifyRes.json();
        throw new Error(error.detail || 'Biometric authentication failed');
      }

      const result = await verifyRes.json();
      
      if (result.verified) {
        toast.success('Biometric login successful!');
        if (result.token) localStorage.setItem('token', result.token);
        onSuccess?.(result);
      }
    } catch (err) {
      console.error('WebAuthn login error:', err);
      const message = err.name === 'NotAllowedError' 
        ? 'Biometric authentication was cancelled'
        : err.message || 'Biometric authentication failed';
      toast.error(message);
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  if (!isSupported) return null;

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleWebAuthnLogin}
      disabled={loading || disabled || !email}
      className="w-full border-white/10 text-white hover:bg-white/5 font-mono uppercase tracking-wider h-11"
      data-testid="webauthn-login-btn"
    >
      {loading ? (
        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
      ) : (
        <Fingerprint className="w-5 h-5 mr-2" />
      )}
      {loading ? 'Authenticating...' : 'Sign in with Biometric'}
    </Button>
  );
};

// WebAuthn Registration Dialog Component
export const WebAuthnRegisterDialog = ({ open, onOpenChange, token, onSuccess }) => {
  const [deviceName, setDeviceName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { isSupported, isPlatformAvailable } = useWebAuthnSupport();

  const handleRegister = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const optionsRes = await fetch(`${API_URL}/api/auth/webauthn/register/options`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({}),
      });

      if (!optionsRes.ok) {
        const error = await optionsRes.json();
        throw new Error(error.detail || 'Failed to get registration options');
      }

      const { options } = await optionsRes.json();
      const credential = await startRegistration(options);

      const verifyRes = await fetch(`${API_URL}/api/auth/webauthn/register/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          credential,
          device_name: deviceName || 'My Device',
        }),
      });

      if (!verifyRes.ok) {
        const error = await verifyRes.json();
        throw new Error(error.detail || 'Registration verification failed');
      }

      const result = await verifyRes.json();
      
      if (result.verified) {
        setSuccess(true);
        toast.success('Biometric credential registered successfully!');
        onSuccess?.();
        setTimeout(() => {
          onOpenChange(false);
          setDeviceName('');
          setSuccess(false);
        }, 1500);
      }
    } catch (err) {
      console.error('WebAuthn registration error:', err);
      const message = err.name === 'NotAllowedError'
        ? 'Registration was cancelled'
        : err.name === 'InvalidStateError'
        ? 'This device is already registered'
        : err.message || 'Registration failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isSupported) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <Fingerprint className="w-5 h-5 text-primary" />
            Register Biometric Credential
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Add fingerprint or face recognition for faster, more secure login
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {!isPlatformAvailable && (
            <div className="flex items-start gap-3 p-3 rounded-sm bg-yellow-500/10 border border-yellow-500/30">
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-400 text-sm font-medium">Limited Support</p>
                <p className="text-white/60 text-xs">
                  Platform authenticator not detected. You may need a security key.
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-center p-6 bg-white/5 rounded-sm">
            <Fingerprint className="w-16 h-16 text-primary" />
          </div>

          <p className="text-white/60 text-sm text-center">
            When prompted, use your device's fingerprint sensor, face recognition, or PIN to register.
          </p>

          <div className="space-y-2">
            <Label htmlFor="deviceName" className="text-white/80">Device Name (optional)</Label>
            <Input
              id="deviceName"
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="My iPhone, Work Laptop, etc."
              className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
              disabled={loading}
              data-testid="webauthn-device-name-input"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 rounded-sm bg-green-500/10 border border-green-500/20">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm">Credential registered successfully!</span>
            </div>
          )}

          <Button
            onClick={handleRegister}
            disabled={loading || success}
            className="w-full bg-primary text-black hover:bg-primary/90 font-mono uppercase tracking-wider"
            data-testid="webauthn-register-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Registering...
              </>
            ) : (
              <>
                <Fingerprint className="w-4 h-4 mr-2" />
                Register Biometric
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// WebAuthn Credentials List Component
export const WebAuthnCredentialsList = ({ token, onUpdate }) => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const fetchCredentials = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/webauthn/credentials`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });

      if (res.ok) {
        const data = await res.json();
        setCredentials(data.credentials || []);
      }
    } catch (err) {
      console.error('Failed to fetch credentials:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredentials();
  }, [token]);

  const handleDelete = async (credentialId) => {
    if (!window.confirm('Are you sure you want to remove this credential?')) return;

    setDeleting(credentialId);
    try {
      const res = await fetch(`${API_URL}/api/auth/webauthn/credentials/${credentialId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });

      if (res.ok) {
        toast.success('Credential removed');
        fetchCredentials();
        onUpdate?.();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to remove credential');
      }
    } catch (err) {
      toast.error('Failed to remove credential');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 text-primary animate-spin" /></div>;

  if (credentials.length === 0) return <p className="text-white/40 text-sm text-center py-4">No biometric credentials registered</p>;

  return (
    <div className="space-y-2">
      {credentials.map((cred) => (
        <div key={cred.credential_id} className="flex items-center justify-between p-3 bg-white/5 rounded-sm">
          <div className="flex items-center gap-3">
            <Smartphone className="w-5 h-5 text-primary" />
            <div>
              <p className="text-white text-sm">{cred.device_name}</p>
              <p className="text-white/40 text-xs">
                Added {new Date(cred.created_at).toLocaleDateString()}
                {cred.last_used && ` • Last used ${new Date(cred.last_used).toLocaleDateString()}`}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(cred.credential_id)}
            disabled={deleting === cred.credential_id}
            className="text-red-400 hover:bg-red-400/10"
            data-testid={`delete-credential-${cred.credential_id}`}
          >
            {deleting === cred.credential_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </Button>
        </div>
      ))}
    </div>
  );
};

export default {
  WebAuthnLoginButton,
  WebAuthnRegisterDialog,
  WebAuthnCredentialsList,
  useWebAuthnSupport,
};
